import { useState, useEffect, useRef } from "react";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarMonth({ events = [], onSelectEvent = null }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const jumped = useRef(false);

  // If the current month is empty, jump once to the month holding the next event
  useEffect(() => {
    if (jumped.current || events.length === 0) return;
    const prefix = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    if (events.some(e => e.date.startsWith(prefix))) { jumped.current = true; return; }
    const todayStr = new Date().toISOString().slice(0, 10);
    const target = events.filter(e => e.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date))[0]
      || events.slice().sort((a, b) => b.date.localeCompare(a.date))[0];
    if (target) {
      const [y, m] = target.date.split("-");
      setCursor(new Date(Number(y), Number(m) - 1, 1));
    }
    jumped.current = true;
  }, [events, cursor]);

  const byDate = events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  const todayIso = iso(new Date());

  const dot = (e) => {
    if (e.type === "appointment") return "bg-[#0F2540]";
    if (e.done) return "bg-emerald-500";
    return e.date < todayIso ? "bg-red-500" : "bg-[#C9A86A]";
  };

  return (
    <div className="glass-card rounded-2xl p-4 sm:p-6" data-testid="calendar-month">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="calendar-prev-month"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="btn-ghost px-3 py-1.5 rounded-lg text-sm"
        >←</button>
        <p className="font-semibold text-gray-900 capitalize" style={{ fontFamily: "Georgia, serif" }} data-testid="calendar-title">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </p>
        <button
          data-testid="calendar-next-month"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="btn-ghost px-3 py-1.5 rounded-lg text-sm"
        >→</button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS.map(d => <div key={d} className="text-[11px] uppercase tracking-wide text-gray-400 text-center py-1">{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map(d => {
          const key = iso(d);
          const dayEvents = byDate[key] || [];
          const outside = d.getMonth() !== cursor.getMonth();
          return (
            <div
              key={key}
              data-testid={`calendar-day-${key}`}
              className={`min-h-[74px] rounded-lg border p-1.5 transition-colors ${
                key === todayIso ? "border-[#C9A86A] bg-[#FFF7E6]" : "border-gray-100"
              } ${outside ? "opacity-40" : ""}`}
            >
              <p className="text-[11px] text-gray-500 mb-1">{d.getDate()}</p>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map(e => (
                  <button
                    key={`${e.type}-${e.id}`}
                    onClick={() => onSelectEvent && onSelectEvent(e)}
                    data-testid={`calendar-event-${e.id}`}
                    className="w-full text-left flex items-start gap-1 group"
                    title={`${e.student_name ? e.student_name + " — " : ""}${e.title}`}
                  >
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${dot(e)}`} />
                    <span className={`text-[10px] leading-tight truncate group-hover:underline ${e.done ? "text-gray-400 line-through" : "text-gray-700"}`}>
                      {e.time ? `${e.time} ` : ""}{e.student_name ? `${e.student_name.split(" ")[0]} · ` : ""}{e.title}
                    </span>
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <p className="text-[10px] text-gray-400">+{dayEvents.length - 3} autre(s)</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C9A86A]" /> Échéance à venir</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> En retard</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Validée</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#0F2540]" /> Rendez-vous</span>
      </div>
    </div>
  );
}
